import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Users, Star, ShoppingCart, Calendar, Plus, Minus, CalendarDays, MapPin as MapPinIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useTrip } from "@/hooks/useTrip";
import { useTrips } from "@/hooks/useTrips";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { TripDisplay, generateSlug } from "@/lib/trips";
import { useMemo, useEffect, useState } from "react";

// Parse itinerary text into structured format
interface Activity {
  time?: string;
  text: string;
  isSubActivity: boolean;
}

interface Day {
  title: string;
  activities: Activity[];
}

const parseItinerary = (itineraryText: string): Day[] => {
  const lines = itineraryText.split('\n').map(line => line.trim());
  const days: Day[] = [];
  let currentDay: Day | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;

    // Check if it's a day header: "Day 1 (Friday)" or "Day 1: Friday"
    const dayMatch = line.match(/^Day\s+(\d+)\s*(?:[\(]([^)]+)[\)])?[:]?\s*(.*)$/i);
    if (dayMatch) {
      if (currentDay) {
        days.push(currentDay);
      }
      currentDay = {
        // Use the third captured group for title, or second if only day in parentheses is present
        title: dayMatch[3] || dayMatch[2] || `Day ${dayMatch[1]}`, 
        activities: []
      };
      continue;
    }

    if (!currentDay) continue;

    // Split the current line into potential activities by '·'
    const parts = line.split('·').map(part => part.trim()).filter(Boolean);

    parts.forEach(part => {
      // Try to extract time from each part: "6:00 AM | Depart Delhi" or "6:00 AM Depart Delhi"
      const timeMatch = part.match(/^(\d{1,2}:\d{2}\s*(?:AM|PM))\s*[|]?\s*(.*)$/i);

      if (timeMatch) {
        currentDay!.activities.push({
          time: timeMatch[1],
          text: timeMatch[2].trim(),
          isSubActivity: false // Assume main activity if time is present
        });
      } else {
        // No time, treat as a regular activity or sub-activity
        currentDay!.activities.push({
          text: part,
          isSubActivity: part.startsWith('○') // Keep explicit sub-activity bullet if present
        });
      }
    });
  }

  if (currentDay) {
    days.push(currentDay);
  }

  return days;
};

const TripDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get trip from route state (passed when navigating from TripCard)
  const tripFromState = location.state?.trip as TripDisplay | undefined;
  
  // If no state, fetch all trips and find by slug
  const { data: allTrips } = useTrips();
  const tripFromSlug = useMemo(() => {
    if (tripFromState || !slug || !allTrips) return null;
    return allTrips.find(trip => generateSlug(trip.title) === slug) || null;
  }, [slug, allTrips, tripFromState]);

  const tripId = tripFromState?.id || tripFromSlug?.id || null;
  const { data: trip, isLoading, error } = useTrip(tripId);
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const { user } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);

  // Use trip from state if available, then from slug match, then fetched trip
  const displayTrip = tripFromState || tripFromSlug || trip;

  const cartQuantity = displayTrip ? getItemQuantity(displayTrip.id) : 0;
  const isInCart = cartQuantity > 0;
  const isFullyBooked = displayTrip?.availability_status === "fully_booked";
  const isFewSlots = displayTrip?.availability_status === "few_slots";

  console.log("Display Trip:", displayTrip);
  console.log("Itinerary content:", displayTrip?.itinerary);

  // Parse group_size to get max quantity
  const getMaxQuantity = (groupSize: string): number => {
    const match = groupSize.match(/(\d+)/g);
    if (match && match.length > 0) {
      const max = parseInt(match[match.length - 1], 10);
      return max || 20;
    }
    return 20;
  };

  const maxQty = displayTrip ? getMaxQuantity(displayTrip.groupSize) : 20;

  // Handle action after successful sign-in (from auth page redirect)
  useEffect(() => {
    const authState = location.state?.fromAuth;
    if (user && displayTrip && authState) {
      if (authState.action === "book") {
        addToCart(displayTrip);
        navigate("/checkout", { replace: true });
      } else if (authState.action === "addToCart") {
        addToCart(displayTrip);
        toast.success(`${displayTrip.title} added to cart!`, {
          description: `${displayTrip.duration} adventure in ${displayTrip.destination}`,
        });
        // Clear the auth state
        navigate(location.pathname, { replace: true, state: { trip: displayTrip } });
      }
    }
  }, [user, displayTrip, location.state, addToCart, navigate, location.pathname]);

  const handleIncreaseQty = () => {
    if (quantity < maxQty) {
      setQuantity(quantity + 1);
    } else {
      toast.info(`Maximum ${maxQty} travelers allowed for this trip`);
    }
  };

  const handleDecreaseQty = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCart = () => {
    if (!displayTrip) return;
    
    if (!user) {
      toast.info("Please sign in to add items to your cart");
      navigate("/auth", { 
        state: { 
          returnTo: `/trip/${slug}`,
          action: "addToCart",
          tripId: displayTrip.id 
        } 
      });
      return;
    }

    if (isInCart) {
      updateQuantity(displayTrip.id, cartQuantity + quantity);
      toast.success(`Added ${quantity} more to cart!`, {
        description: `${displayTrip.title} - Total: ${cartQuantity + quantity}`,
      });
    } else {
      addToCart(displayTrip, quantity);
      toast.success(`${displayTrip.title} added to cart!`, {
        description: `Quantity: ${quantity} × ${displayTrip.duration} adventure in ${displayTrip.destination}`,
      });
    }
    setQuantity(1);
  };

  const handleBookNow = () => {
    if (!displayTrip) return;
    
    if (!user) {
      toast.info("Please sign in to book your trip");
      navigate("/auth", { 
        state: { 
          returnTo: `/trip/${slug}`,
          action: "book",
          tripId: displayTrip.id 
        } 
      });
      return;
    }

    if (isInCart) {
      updateQuantity(displayTrip.id, cartQuantity + quantity);
    } else {
      addToCart(displayTrip, quantity);
    }
    setQuantity(1);
    navigate("/checkout");
  };

  if (isLoading && !tripFromState) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Skeleton className="h-96 w-full rounded-xl mb-6" />
              <Skeleton className="h-8 w-3/4 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-6" />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !displayTrip) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Trip not found</h1>
            <p className="text-muted-foreground mb-6">
              The trip you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate("/")}>Go Back Home</Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!displayTrip) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Trip not found</h1>
            <p className="text-muted-foreground mb-6">
              The trip you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate("/")}>Go Back Home</Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen pt-24 pb-12 bg-background">
        {/* Hero Image Section */}
        <div className="relative h-[60vh] md:h-[70vh] overflow-hidden mb-8 bg-muted">
          {imageError ? (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <div className="text-center p-8">
                <div className="text-6xl mb-4">🖼️</div>
                <p className="text-lg text-muted-foreground">Image not available</p>
                <p className="text-sm text-muted-foreground/70 mt-2">Please update the image URL in admin panel</p>
              </div>
            </div>
          ) : (
            <img
              src={displayTrip.image}
              alt={displayTrip.title}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
              onLoad={() => setImageError(false)}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/40 to-transparent" />
          
          {/* Back Button */}
          <div className="absolute top-4 left-4">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => navigate(-1)}
              className="bg-background/90 backdrop-blur-sm hover:bg-background"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </div>

          {/* Category and Availability Status Badges */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Badge className="bg-primary text-primary-foreground text-sm px-4 py-2">
              {displayTrip.category}
            </Badge>
            {displayTrip.availability_status && (
              <Badge
                className={`text-sm px-4 py-2 ${
                  displayTrip.availability_status === "available"
                    ? "bg-green-500/90 text-white border-green-600"
                    : displayTrip.availability_status === "few_slots"
                    ? "bg-orange-500/90 text-white border-orange-600"
                    : "bg-red-500/90 text-white border-red-600"
                }`}
              >
                {displayTrip.availability_status === "available"
                  ? "Available"
                  : displayTrip.availability_status === "few_slots"
                  ? "Few Slots Left"
                  : "Fully Booked"}
              </Badge>
            )}
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container mx-auto max-w-4xl">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 fill-accent text-accent" />
                <span className="text-lg font-semibold text-primary-foreground">
                  {displayTrip.rating}
                </span>
              </div>
              <h1 className="font-display text-3xl md:text-5xl font-bold text-primary-foreground mb-4">
                {displayTrip.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-primary-foreground/90">
                <span className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {displayTrip.destination}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {displayTrip.duration}
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {displayTrip.groupSize}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="md:col-span-2 space-y-6">
                {/* Description */}
                <section>
                  <h2 className="font-display text-2xl font-bold mb-4">About this trip</h2>
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    {displayTrip.description}
                  </p>
                </section>

                {/* Highlights */}
                <section>
                  <h2 className="font-display text-2xl font-bold mb-4">Highlights</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {displayTrip.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border"
                      >
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-foreground">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Itinerary */}
                {displayTrip.itinerary && (
                  <section>
                    <h2 className="font-display text-2xl font-bold mb-6">Itinerary</h2>
                    <div className="space-y-8">
                      {(() => {
                        try {
                          const parsedDays = parseItinerary(displayTrip.itinerary || "");
                          if (parsedDays.length === 0) {
                            return (
                              <div className="text-muted-foreground">
                                <p>No itinerary data available.</p>
                              </div>
                            );
                          }
                          return parsedDays.map((day, dayIndex) => (
                            <div key={dayIndex} className="relative">
                              {/* Day Header */}
                              <div className="flex items-center gap-3 mb-4">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold text-lg">
                                  {dayIndex + 1}
                                </div>
                                <h3 className="font-display text-xl font-semibold text-foreground">
                                  {day.title}
                                </h3>
                              </div>

                              {/* Timeline */}
                              <div className="relative pl-6 ml-5 border-l-2 border-primary/30">
                                {day.activities.map((activity, actIndex) => (
                                  <div key={actIndex} className="relative mb-6 last:mb-0">
                                    {/* Timeline Dot */}
                                    <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full bg-primary border-4 border-background" />
                                    
                                    {/* Activity Content */}
                                    <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                                      {activity.time && (
                                        <div className="flex items-center gap-2 mb-2">
                                          <Clock className="w-4 h-4 text-primary" />
                                          <span className="font-semibold text-foreground text-sm">
                                            {activity.time}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex items-start gap-2">
                                        {activity.isSubActivity ? (
                                          <span className="text-muted-foreground mt-1">○</span>
                                        ) : (
                                          <span className="text-primary mt-1 font-bold">●</span>
                                        )}
                                        <div className="flex-1">
                                          <p className="text-foreground leading-relaxed">
                                            {activity.text}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ));
                        } catch (error) {
                          console.error("Error parsing itinerary:", error);
                          return (
                            <div className="text-muted-foreground p-4 border border-border rounded-lg">
                              <p>Unable to parse itinerary. Please check the format.</p>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </section>
                )}
              </div>

              {/* Booking Sidebar */}
              <div className="md:col-span-1">
                <div className="sticky top-24 bg-card border border-border rounded-xl p-6 shadow-lg">
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold text-foreground">
                        ₹{displayTrip.price.toLocaleString("en-IN")}
                      </span>
                      <span className="text-muted-foreground">/person</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Prices may vary based on availability
                    </p>
                  </div>

                  {/* Quantity Selector */}
                  <div className="mb-6">
                    <Label className="text-sm font-medium mb-3 block">Number of Travelers</Label>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 border border-border rounded-lg">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-10 w-10 p-0 hover:bg-muted"
                          onClick={handleDecreaseQty}
                          disabled={quantity <= 1 || isFullyBooked}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-12 text-center text-lg font-semibold text-foreground">
                          {quantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-10 w-10 p-0 hover:bg-muted"
                          onClick={handleIncreaseQty}
                          disabled={quantity >= maxQty || isFullyBooked}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">
                          Max: {maxQty} travelers
                        </p>
                        {isFullyBooked && (
                          <p className="text-sm font-medium text-red-600 mt-1">
                            This trip is fully booked.
                          </p>
                        )}
                        {isFewSlots && !isFullyBooked && (
                          <p className="text-sm font-medium text-orange-600 mt-1">
                            Limited availability! Book your spot soon.
                          </p>
                        )}
                        {isInCart && !isFullyBooked && (
                          <p className="text-xs text-primary">
                            Already in cart: {cartQuantity}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Subtotal</span>
                        <span className="text-lg font-bold text-foreground">
                          ₹{(displayTrip.price * quantity).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={handleBookNow}
                      className="w-full h-12 text-base font-semibold"
                      size="lg"
                      disabled={isFullyBooked}
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      {isFullyBooked ? "Fully Booked" : "Book Now"}
                    </Button>
                    <Button
                      onClick={handleAddToCart}
                      variant="outline"
                      className="w-full h-12 text-base"
                      size="lg"
                      disabled={isFullyBooked}
                    >
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      {isFullyBooked ? "Fully Booked" : isInCart ? "Add More to Cart" : "Add to Cart"}
                    </Button>
                  </div>

                  {/* Trip Info */}
                  <div className="mt-6 pt-6 border-t border-border space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Duration</span>
                      <span className="text-sm font-medium">{displayTrip.duration}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Group Size</span>
                      <span className="text-sm font-medium">{displayTrip.groupSize}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Rating</span>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-accent text-accent" />
                        <span className="text-sm font-medium">{displayTrip.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TripDetail;

